import React, { useEffect, useMemo } from 'react';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { ProductDataForm } from '../../../../app/models/Product';
import { Unit } from '../../../../app/models/Unit';
import { ItemCategory } from '../../../../app/models/ItemCategory';
import { Location } from '../../../../app/models/Location';
import { Sector } from '../../../../app/models/Sector';
import { Quality } from '../../../../app/models/Quality';
import { Color } from '../../../../app/models/Color';

type SelectOpt = { id: number | string; name: string };

type Props = {
  productDataForm: ProductDataForm;
  setProductDataForm: (productDataForm: ProductDataForm) => void;
  units: Unit[];
  ownershipTypes: SelectOpt[];
  taxes: SelectOpt[];
  sectors: Sector[];
  categories: ItemCategory[];
  locations: Location[];
  qualities: Quality[];
  colors: Color[];
  errorFields?: any;
  is_edit?: boolean;
  /** Contenido opcional a mostrar justo debajo del campo Nombre (ej. botón Receta) */
  afterNameContent?: React.ReactNode;
};

const ProductForm = ({
  productDataForm,
  setProductDataForm,
  units,
  ownershipTypes,
  taxes,
  sectors,
  categories,
  locations,
  qualities,
  colors,
  errorFields,
  is_edit = false,
  afterNameContent
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => { setErrors(errorFields); }, [errorFields]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as any;

    // Manejo especial para switch Aplica Cortes
    if (type === 'checkbox' && name === 'applies_cuts') {
      setProductDataForm({
        ...productDataForm,
        applies_cuts: checked,
        // Limpiar cuts_count cuando se desmarca
        cuts_count: checked ? productDataForm.cuts_count : null
      });
      return;
    }

    const casted =
      type === 'checkbox' ? checked :
      name === 'stock' || name === 'cuts_count' ? (value === '' ? null : Number(value)) :
      value;

    setProductDataForm({ ...productDataForm, [name]: casted } as any);
  };

  const handleSelect = (name: keyof ProductDataForm, value: any) => {
    setProductDataForm({ ...productDataForm, [name]: (value ?? null) as any });
  };

  const taxOptions = useMemo(() => taxes.map(t => ({ value: t.id, label: t.name })), [taxes]);

  // Solo bloquear campos cuando estamos editando Y el stock es mayor que 0
  const isLockedByStock = is_edit && (Number(productDataForm.stock ?? 0) > 0);

  return (
    <>
      {/* --------- General --------- */}
      <h6 className="mt-2">Sección General</h6>
      <div className="mb-2 row">
        <div className="col-md-12">
          <label className="col-form-label">Nombre</label>
          <input
            name="name" id="name" type="text"
            className={`form-control ${fieldHasError('name')}`}
            value={productDataForm.name ?? ''}
            onChange={handleChange} onFocus={() => onFocusRemove('name')}
            placeholder="Nombre del producto"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('name')}</div>
        </div>
      </div>
      {afterNameContent != null ? afterNameContent : <></>}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">
            Sector {!is_edit && <span className="text-danger">*</span>}
          </label>
          <SelectTwo
            name="sector_id" id="sector_id"
            inputValue={productDataForm.sector_id ?? null}
            options={SelectTwoMapperOptions(sectors, 'id', ['name'])}
            hasError={fieldHasError('sector_id') !== ''}
            onChange={(v: any) => handleSelect('sector_id', v?.value)}
            placeholder="Seleccione sector"
            onFocus={() => onFocusRemove('sector_id')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('sector_id')}</div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label">
            Categoría {!is_edit && <span className="text-danger">*</span>}
          </label>
          <SelectTwo
            name="category_id" id="category_id"
            inputValue={productDataForm.category_id ?? null}
            options={SelectTwoMapperOptions(categories, 'id', ['name'])}
            hasError={fieldHasError('category_id') !== ''}
            onChange={(v: any) => handleSelect('category_id', v?.value)}
            placeholder="Seleccione categoría"
            onFocus={() => onFocusRemove('category_id')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('category_id')}</div>
        </div>
      </div>

      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Producto</label>
          <SelectTwo
            name="ownership_type" id="ownership_type"
            inputValue={productDataForm.ownership_type ?? null}
            options={ownershipTypes.map(o => ({ value: o.id, label: o.name }))}
            hasError={fieldHasError('ownership_type') !== ''}
            onChange={(v: any) => handleSelect('ownership_type', v?.value)}
            placeholder="Seleccione tipo"
            onFocus={() => onFocusRemove('ownership_type')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('ownership_type')}</div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label">Origen</label>
          <SelectTwo
            name="origin_id" id="origin_id"
            inputValue={productDataForm.origin_id ?? null}
            options={SelectTwoMapperOptions(locations, 'id', ['name'])}
            hasError={fieldHasError('origin_id') !== ''}
            onChange={(v: any) => handleSelect('origin_id', v?.value)}
            placeholder="Seleccione ubicación"
            onFocus={() => onFocusRemove('origin_id')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('origin_id')}</div>
        </div>
      </div>

      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Calidad</label>
          <SelectTwo
            name="quality_id"
            id="quality_id"
            inputValue={productDataForm.quality_id ?? null}
            options={SelectTwoMapperOptions(qualities, 'id', ['name'])}
            hasError={fieldHasError('quality_id') !== ''}
            onChange={(v: any) => handleSelect('quality_id', v?.value)}
            placeholder="Seleccione calidad"
            onFocus={() => onFocusRemove('quality_id')}
            disabled={isLockedByStock}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('quality_id')}</div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label">Color</label>
          <SelectTwo
            name="color_id"
            id="color_id"
            inputValue={productDataForm.color_id ?? null}
            options={SelectTwoMapperOptions(colors, 'id', ['name'])}
            hasError={fieldHasError('color_id') !== ''}
            onChange={(v: any) => handleSelect('color_id', v?.value)}
            placeholder="Seleccione color"
            onFocus={() => onFocusRemove('color_id')}
            disabled={isLockedByStock}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('color_id')}</div>
        </div>
      </div>

      <div className="mb-2 row">
        <div className="col-md-6 d-flex align-items-end pb-2">
          <div className="square-switch d-flex form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="applies_cuts"
              name="applies_cuts"
              checked={!!productDataForm.applies_cuts}
              onChange={handleChange}
              disabled={isLockedByStock}
            />
            <label className="form-check-label my-auto ms-2" htmlFor="applies_cuts">Aplica Cortes</label>
          </div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label">N° de Cortes</label>
          <input
            name="cuts_count"
            id="cuts_count"
            type="number"
            className={`form-control ${fieldHasError('cuts_count')}`}
            value={productDataForm.applies_cuts ? (productDataForm.cuts_count ?? '') : ''}
            onChange={handleChange}
            onFocus={() => onFocusRemove('cuts_count')}
            placeholder="0"
            disabled={!productDataForm.applies_cuts || isLockedByStock}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('cuts_count')}</div>
        </div>
      </div>

      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Stock</label>
          <input
            name="stock"
            id="stock"
            type="number"
            className={`form-control ${fieldHasError('stock')}`}
            value={productDataForm.stock ?? 0}
            onChange={handleChange}
            onFocus={() => onFocusRemove('stock')}
            placeholder="0"
            readOnly={isLockedByStock}
            disabled={isLockedByStock}
            style={isLockedByStock ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : undefined}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('stock')}</div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label">
            Unidad de Medida {!is_edit && <span className="text-danger">*</span>}
          </label>
          <SelectTwo
            name="unit_id"
            id="unit_id"
            inputValue={productDataForm.unit_id ?? null}
            options={SelectTwoMapperOptions(units, 'id', ['name'])}
            hasError={fieldHasError('unit_id') !== ''}
            onChange={(v: any) => handleSelect('unit_id', v?.value)}
            placeholder="Seleccione unidad"
            onFocus={() => onFocusRemove('unit_id')}
            disabled={isLockedByStock}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('unit_id')}</div>
        </div>
      </div>

      {/* --------- Venta --------- */}
      <h6 className="mt-3">Sección Venta</h6>
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Nombre de Venta</label>
          <input
            name="sale_name" id="sale_name" type="text"
            className={`form-control ${fieldHasError('sale_name')}`}
            value={productDataForm.sale_name ?? ''}
            onChange={handleChange} onFocus={() => onFocusRemove('sale_name')}
            placeholder="Nombre que aparecerá en documentos de venta"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('sale_name')}</div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Impuestos (Venta)</label>
          <SelectTwo
            name="sale_taxes" id="sale_taxes"
            inputValue={(productDataForm.sale_taxes ?? []) as any}
            options={taxOptions}
            isMulti
            hasError={fieldHasError('sale_taxes') !== ''}
            onChange={(vals: any[]) => handleSelect('sale_taxes', (vals ?? []).map(v => v.value))}
            placeholder="Seleccione impuestos"
            onFocus={() => onFocusRemove('sale_taxes')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('sale_taxes')}</div>
        </div>
      </div>

      {/* --------- Compra --------- */}
      <h6 className="mt-3">Sección Compras</h6>
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Nombre de Compra</label>
          <input
            name="purchase_name" id="purchase_name" type="text"
            className={`form-control ${fieldHasError('purchase_name')}`}
            value={productDataForm.purchase_name ?? ''}
            onChange={handleChange} onFocus={() => onFocusRemove('purchase_name')}
            placeholder="Nombre que aparecerá en documentos de compra"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('purchase_name')}</div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Impuestos (Compra)</label>
          <SelectTwo
            name="purchase_taxes" id="purchase_taxes"
            inputValue={(productDataForm.purchase_taxes ?? []) as any}
            options={taxOptions}
            isMulti
            hasError={fieldHasError('purchase_taxes') !== ''}
            onChange={(vals: any[]) => handleSelect('purchase_taxes', (vals ?? []).map(v => v.value))}
            placeholder="Seleccione impuestos"
            onFocus={() => onFocusRemove('purchase_taxes')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('purchase_taxes')}</div>
        </div>
      </div>
    </>
  );
};

export default ProductForm;